import * as ed from '@noble/ed25519';
import { z } from 'zod';

import { TenantId, Uuid } from '../schema/primitives.js';
import { signedMessageBytes } from './canonical.js';
import { KeyId } from './types.js';

/**
 * Signed revocation list — the set of license ids revoked for a tenant, so a
 * SPECIFIC leaked license.json can be killed without disabling the tenant, and
 * offline / cached-license devices can honor it once they've fetched (and
 * cached) the list. Scoped per-tenant to keep each list small; signed with the
 * same Ed25519 keyring as licenses (so a rotated key works here too).
 *
 * Ed25519 hex signature over the canonical JSON of all other fields — same
 * scheme as {@link License}.
 */
export const RevocationList = z.object({
  tenantId: TenantId,
  /** Revoked license ids (License.id values). */
  revokedIds: z.array(Uuid),
  /** When this list was minted (unix sec) — surfaced for staleness diagnostics. */
  issuedAt: z.number().int().nonnegative(),
  /** Which signing key signed the list; absent → default/legacy key. */
  keyId: KeyId.optional(),
  signature: z
    .string()
    .length(128)
    .regex(/^[0-9a-f]{128}$/, 'signature must be 128 lowercase hex chars'),
});
export type RevocationList = z.infer<typeof RevocationList>;

export const UnsignedRevocationList = RevocationList.omit({ signature: true });
export type UnsignedRevocationList = z.infer<typeof UnsignedRevocationList>;

/** Sign a revocation list with a 32-byte Ed25519 private key. */
export async function signRevocationList(input: {
  privateKey: Uint8Array;
  unsigned: UnsignedRevocationList;
}): Promise<RevocationList> {
  const parsed = UnsignedRevocationList.parse(input.unsigned);
  const sig = await ed.signAsync(signedMessageBytes(parsed), input.privateKey);
  return RevocationList.parse({ ...parsed, signature: ed.etc.bytesToHex(sig) });
}

/**
 * Verify a revocation list's signature + tenant binding, returning it (or null
 * on any failure — malformed, wrong tenant, unknown keyId, bad signature).
 * Never throws. Deliberately does NOT reject on staleness: revocation is
 * fail-open (an old-but-valid cached list is better than none), so the caller
 * decides how much to trust `issuedAt`.
 */
export async function verifyRevocationList(input: {
  publicKey: Uint8Array;
  keyring?: Record<string, Uint8Array>;
  list: unknown;
  expectedTenantId: string;
}): Promise<RevocationList | null> {
  try {
    const parsed = RevocationList.safeParse(input.list);
    if (!parsed.success) return null;
    const list = parsed.data;
    if (list.tenantId !== input.expectedTenantId) return null;

    const publicKey = list.keyId !== undefined ? input.keyring?.[list.keyId] : input.publicKey;
    if (publicKey === undefined) return null;

    const sigBytes = ed.etc.hexToBytes(list.signature);
    const ok = await ed.verifyAsync(sigBytes, signedMessageBytes(list), publicKey);
    return ok ? list : null;
  } catch {
    return null;
  }
}
